import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { NewTicketPopupResultT } from 'contexts/TicketsContext';
import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { WipRecordI } from 'declarations/interfaces/wip';
import { clearWipSelectedIds } from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import { mapWipRecordToChirpTicketPrefill } from '../helpers/mapWipToChirpPrefill';

export interface WipChirpTicketQueueI {
  /** Whether the abort-confirmation dialog (multi-item session) should be visible. */
  isAbortConfirmOpen: boolean;
  /** True while at least one record is queued (used to drive button busy states). */
  isSessionActive: boolean;
  /** Total number of records the user kicked off the session with. */
  sessionTotal: number;
  /** 1-based index of the record currently being processed (0 when no session). */
  currentPosition: number;
  /** Starts a new ticket creation session for the supplied records (no-op when empty). */
  startQueue: (records: readonly WipRecordI[]) => void;
  /** Cancels the abort confirm without ending the session. */
  cancelAbort: () => void;
  /** Confirms the abort: closes the popup and clears the session. */
  confirmAbort: () => void;
}

/**
 * Mirrors WarrQueue's WIP -> Create Chirp Ticket queue lifecycle:
 *   - User selects 1+ rows and clicks Create Chirp.
 *   - Each selected record auto-populates the Create Ticket popup in sequence.
 *   - On submit success: advance to the next record (or finish and clear selection).
 *   - On user-initiated close mid-session (length > 1): confirm abort before closing.
 *   - On user-initiated close for single-item session: just close.
 *   - On submit error: leave the popup open so the user can correct + retry.
 */
export const useWipChirpTicketQueue = (onSessionComplete?: () => void): WipChirpTicketQueueI => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { openNewTicketPopupWithPrefill, setNewTicketPopupVisibility } = useTickets();

  const [queue, setQueue] = useState<WipRecordI[]>([]);
  const [queueIndex, setQueueIndex] = useState<number>(0);
  const [sessionTotal, setSessionTotal] = useState<number>(0);
  const [isAbortConfirmOpen, setIsAbortConfirmOpen] = useState<boolean>(false);

  // We have to expose a single, stable result handler to the popup but the logic depends on
  // current queue state. We mirror the WarrQueue ref-pattern here so updates land immediately
  // without re-registering the handler each render.
  const queueRef = useRef<WipRecordI[]>([]);
  const queueIndexRef = useRef<number>(0);
  const sessionTotalRef = useRef<number>(0);

  useEffect((): void => {
    queueRef.current = queue;
  }, [queue]);
  useEffect((): void => {
    queueIndexRef.current = queueIndex;
  }, [queueIndex]);
  useEffect((): void => {
    sessionTotalRef.current = sessionTotal;
  }, [sessionTotal]);

  const clearSession = useCallback((): void => {
    setQueue([]);
    setQueueIndex(0);
    setSessionTotal(0);
    setIsAbortConfirmOpen(false);
  }, []);

  const handleResult = useCallback(
    (result: NewTicketPopupResultT): void => {
      if (result === 'success') {
        const currentQueue = queueRef.current;
        const currentIndex = queueIndexRef.current;

        if (currentIndex < currentQueue.length - 1) {
          // Advance to next record. Push next prefill into the still-open popup.
          const nextIndex = currentIndex + 1;
          setQueueIndex(nextIndex);
          openNewTicketPopupWithPrefill(
            mapWipRecordToChirpTicketPrefill(currentQueue[nextIndex]),
            handleResult
          );
          return;
        }

        // Last record completed — refresh the WIP list (records now have linked tickets),
        // clear selection, close popup, and end the session.
        queryClient.invalidateQueries([RequestQueryKeysE.FetchWipRecords]);
        queryClient.invalidateQueries([RequestQueryKeysE.FetchWipDealerSummary]);
        dispatch(clearWipSelectedIds());
        setNewTicketPopupVisibility(false);
        clearSession();
        onSessionComplete?.();
        return;
      }

      if (result === 'cancel') {
        // Multi-item sessions ask for confirmation before discarding remaining records.
        if (sessionTotalRef.current > 1) {
          setIsAbortConfirmOpen(true);
          return;
        }
        setNewTicketPopupVisibility(false);
        clearSession();
        return;
      }

      // 'error' — leave the popup open so the user can adjust and retry.
    },
    [
      clearSession,
      dispatch,
      onSessionComplete,
      openNewTicketPopupWithPrefill,
      queryClient,
      setNewTicketPopupVisibility,
    ]
  );

  const startQueue = useCallback(
    (records: readonly WipRecordI[]): void => {
      if (records.length === 0) return;
      const queueCopy = [...records];
      setQueue(queueCopy);
      setQueueIndex(0);
      setSessionTotal(queueCopy.length);
      setIsAbortConfirmOpen(false);
      queueRef.current = queueCopy;
      queueIndexRef.current = 0;
      sessionTotalRef.current = queueCopy.length;
      openNewTicketPopupWithPrefill(mapWipRecordToChirpTicketPrefill(queueCopy[0]), handleResult);
    },
    [handleResult, openNewTicketPopupWithPrefill]
  );

  const cancelAbort = useCallback((): void => {
    setIsAbortConfirmOpen(false);
  }, []);

  const confirmAbort = useCallback((): void => {
    setNewTicketPopupVisibility(false);
    clearSession();
  }, [clearSession, setNewTicketPopupVisibility]);

  // Defensive cleanup: if the WIP page unmounts mid-session, close the popup so the TicketsContext
  // does not keep a dangling prefill + result handler that points at this unmounted component.
  useEffect((): (() => void) => {
    return (): void => {
      if (sessionTotalRef.current > 0) {
        setNewTicketPopupVisibility(false);
      }
    };
  }, [setNewTicketPopupVisibility]);

  return {
    isAbortConfirmOpen,
    isSessionActive: sessionTotal > 0,
    sessionTotal,
    currentPosition: sessionTotal > 0 ? queueIndex + 1 : 0,
    startQueue,
    cancelAbort,
    confirmAbort,
  };
};
